/**
 * Ted #28 Phase 1b (#1050) — the TRANSPORT line item.
 *
 * Ted (HTHD, 2026-09-21): Gingr has "one transportation line item at $15,
 * and another at $30, but the higher amount is really dependent on the type
 * of transportation — a vet appointment, daycare pickup outside our normal
 * radius, or another special trip." He wants the line item in the system
 * "but still allow the price to be overridden when necessary", and
 * transport "is not something we actively offer as a new service anymore;
 * it's primarily for existing clients."
 *
 * Transport is a tenant-owned `service_types` row (Settings → Services sets
 * the price, #933), added to a stay as a SERVICE LEG (#984). Two things
 * make it unlike a nail trim, and both are keyed on the service NAME the
 * way `FACILITY_HEADCOUNT_SERVICE_NAMES` keys the floor rule — names are
 * stable across tenants, ids are not:
 *
 *  1. It is LEG-ONLY. Never a reservation of its own (the van does not book
 *     a kennel), and never in the customer wizard (Ted does not sell it to
 *     new clients). The desk adds it to an existing visit, full stop.
 *  2. Adding it SETS THE FLAG. A transport leg carries a direction, and the
 *     stay's `transport` mode (#1054) is updated from it, so the hard stop
 *     at check-in fires from the line item as well as from the reservation
 *     form. The reverse is NOT true: the flag never depends on a charge.
 *     Ted folds transport into some grandfathered clients' daycare price,
 *     and those dogs still arrive by van.
 *
 * Pure: no I/O. The engine, the customer router and Settings all read this
 * one list; a fourth copy anywhere else is the drift this file exists to
 * prevent.
 */
import { normalizeTransport, type TransportMode } from '../dog-profile/dog-flags.js';

/** `transport` = the everyday trip; `transport-trip` = the priced-per-trip one (vet, out of area). */
export const TRANSPORT_SERVICE_NAMES: readonly string[] = ['transport', 'transport-trip'];

export function isTransportServiceName(serviceName: string): boolean {
  return TRANSPORT_SERVICE_NAMES.includes(serviceName);
}

/**
 * Services that exist only as a line on a stay. Today that is transport;
 * the list is separate from TRANSPORT_SERVICE_NAMES on purpose, because
 * "leg-only" (no capacity, no customer listing) and "sets the transport
 * flag" are two rules that happen to share members.
 */
export const LEG_ONLY_SERVICE_NAMES: readonly string[] = [...TRANSPORT_SERVICE_NAMES];

export function isLegOnlyServiceName(serviceName: string): boolean {
  return LEG_ONLY_SERVICE_NAMES.includes(serviceName);
}

/** The customer wizard's view of a catalogue: everything the customer may book alone. */
export function customerBookableServiceTypes<T extends { name: string }>(list: readonly T[]): T[] {
  return list.filter((s) => !isLegOnlyServiceName(s.name));
}

/** A leg's direction — a transport leg always has one. 'none' is not a trip. */
export type TransportLegDirection = Exclude<TransportMode, 'none'>;

/**
 * The stay's mode after a transport leg is added: a UNION, never a
 * replacement. A stay already flagged "ride in" that gains a "ride home"
 * leg is now both ways; adding the same direction twice changes nothing.
 * Nothing here ever lowers a mode — that is Edit Reservation's job, on
 * purpose, because a lowered flag is the unsafe direction for the hard stop.
 */
export function mergeTransportModes(
  current: string | null | undefined,
  added: TransportLegDirection,
): TransportMode {
  const cur = normalizeTransport(current);
  if (cur === 'none') return added;
  if (cur === added) return cur;
  return 'both';
}
